import {useEffect, useState} from 'react'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import useCrispChat from 'app/Hooks/useCrispChat'
import useAuditContext from 'app/Hooks/useAuditContext'

const ControlCard = () => {
  const {currentUser} = useAuth()
  const [totalControl, setTotalcontrols] = useState(0)
  const [totalISO, setTotalISO] = useState(0)
  const [totalSOC, setTotalSOC] = useState(0)
  const [unhealthyControlsCount, setUnhealthyControlsCount] = useState(0)
  const [unhealthyISOControlsCount, setUnhealthyISOControlsCount] = useState(0)
  const [unhealthySOCControlsCount, setUnhealthySOCControlsCount] = useState(0)
  const [healthyControlsCount, setHealthyControlsCount] = useState(0)
  const [healthyISOControlsCount, setHealthyISOControlsCount] = useState(0)
  const [healthySOCControlsCount, setHealthySOCControlsCount] = useState(0)
  const [controls, setControls] = useState<any[]>([])
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const {toggleCrispChat} = useCrispChat()
  const [totalGDPR, setTotalGDPR] = useState(0)
  const [healthyGDPRControlsCount, setHealthyGDPRControlsCount] = useState(0)
  const [unhealthyGDPRControlsCount, setUnhealthyGDPRControlsCount] = useState(0)
  const [totalHIPAA, setTotalHIPAA] = useState(0)
  const [healthyHIPAAControlsCount, setHealthyHIPAAControlsCount] = useState(0)
  const [unhealthyHIPAAControlsCount, setUnhealthyHIPAAControlsCount] = useState(0)
  const [audits, setAudits] = useState<any[]>([])
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor,
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((audit) => audit.status !== 'Completed')

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => audit.auditor?.id === currentUser?.uid)
      }
      setAudits(Data)
    })

    return unsubscribe
  }, [currentUser?.tenantId, currentUser?.role, currentUser?.uid])

  useEffect(() => {
    if (activeFrameworks.length > 0 || (currentUser?.role === 'Auditor' && audits.length > 0)) {
      getControls()
    }
  }, [activeFrameworks, audits, currentUser?.role])

  useEffect(() => {
    getActiveFramworks()
  }, [currentUser?.tenantId])

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  const getControls = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'controls')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().control_framework_mappings,
          status: doc.data().control_evidence_status,
        }))

        const filteredControls = controlsData.filter((control) => {
          if (!control.framework) return false
          if (control.status === 'Deactivated') return false

          if (currentUser?.role === 'Auditor' && audits.length > 0) {
            const auditFrameworks = audits.flatMap((audit) => audit.framework || [])
            if (auditFrameworks.length === 0) return false

            return auditFrameworks.some(
              (framework) =>
                control.framework.startsWith(framework) &&
                !control.framework.startsWith('ISO 27001:2013')
            )
          } else if (currentAuditContext) {
            return currentAuditContext.framework.some(
              (framework) =>
                control.framework.startsWith(framework) &&
                !control.framework.startsWith('ISO 27001:2013')
            )
          } else {
            return activeFrameworks.some(
              (framework) =>
                control.framework.startsWith(framework) &&
                !control.framework.startsWith('ISO 27001:2013')
            )
          }
        })

        setControls(filteredControls)
        setTotalcontrols(filteredControls.length)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    const unhealthyControls = controls.filter((doc) => {
      const status = doc.control_evidence_status

      return status && status === 'Unhealthy' && status !== 'Deactivated'
    })

    const unhealthyControlsCount = unhealthyControls.length

    setUnhealthyControlsCount(unhealthyControlsCount)

    const iso27001Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('ISO') && !framework.startsWith('ISO 27001:2013')
    })

    const iso27001ControlsCount = iso27001Controls.length
    setTotalISO(iso27001ControlsCount)

    const SOC2Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.startsWith('SOC')
    })

    const SOC2ControlsCount = SOC2Controls.length
    setTotalSOC(SOC2ControlsCount)

    const HIPAAControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('HIPAA')
    })

    const HIPAAControlsCount = HIPAAControls.length
    setTotalHIPAA(HIPAAControlsCount)
  }, [controls])

  useEffect(() => {
    if (!controls || controls.length === 0) {
      return
    }

    const activeControls = controls.filter((doc) => doc.control_evidence_status !== 'Deactivated')
    const HealthyControls = activeControls.filter((doc) => {
      const status = doc.control_evidence_status || ''
      return status.toLowerCase() === 'ok'
    })
    const HealthyControlsCount = HealthyControls.length
    setHealthyControlsCount(HealthyControlsCount)

    const isoHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return (
        framework.includes('ISO') &&
        status.toLowerCase() === 'ok' &&
        !framework.startsWith('ISO 27001:2013')
      )
    })

    const isoHealthyControlsCount = isoHealthyControls.length

    setHealthyISOControlsCount(isoHealthyControlsCount)

    const socHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('SOC') && status.toLowerCase() === 'ok'
    })

    const socHealthyControlsCount = socHealthyControls.length

    setHealthySOCControlsCount(socHealthyControlsCount)

    const hipaaHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('HIPAA') && status.toLowerCase() === 'ok'
    })

    const hipaaHealthyControlsCount = hipaaHealthyControls.length

    setHealthyHIPAAControlsCount(hipaaHealthyControlsCount)
  }, [controls])

  useEffect(() => {
    const isoUnhealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      const status = doc.control_evidence_status
      return (
        framework &&
        framework.startsWith('ISO') &&
        status === 'Unhealthy' &&
        !framework.startsWith('ISO 27001:2013')
      )
    })

    const isoUnhealthyControlsCount = isoUnhealthyControls.length
    setUnhealthyISOControlsCount(isoUnhealthyControlsCount)

    const socUnhealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      const status = doc.control_evidence_status
      return framework && framework.startsWith('SOC') && status === 'Unhealthy'
    })

    const socUnhealthyControlsCount = socUnhealthyControls.length

    setUnhealthySOCControlsCount(socUnhealthyControlsCount)

    const hipaaUnhealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      const status = doc.control_evidence_status
      return framework && framework.includes('HIPAA') && status === 'Unhealthy'
    })

    const hipaaUnhealthyControlsCount = hipaaUnhealthyControls.length

    setUnhealthyHIPAAControlsCount(hipaaUnhealthyControlsCount)
  }, [controls])

  useEffect(() => {
    const GDPRControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('GDPR')
    })

    const GDPRControlsCount = GDPRControls.length
    setTotalGDPR(GDPRControlsCount)
  }, [controls])

  useEffect(() => {
    if (!controls || controls.length === 0) {
      return
    }

    const gdprHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('GDPR') && status.toLowerCase() === 'ok'
    })

    const gdprHealthyControlsCount = gdprHealthyControls.length
    setHealthyGDPRControlsCount(gdprHealthyControlsCount)
  }, [controls])

  useEffect(() => {
    const gdprUnhealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      const status = doc.control_evidence_status
      return framework && framework.includes('GDPR') && status === 'Unhealthy'
    })

    const gdprUnhealthyControlsCount = gdprUnhealthyControls.length
    setUnhealthyGDPRControlsCount(gdprUnhealthyControlsCount)
  }, [controls])

  const renderFrameworkCard = (framework: string | never) => {
    let totalControls, healthyControlsCount, unhealthyControlsCount

    switch (framework) {
      case 'ISO 27001:2022':
        totalControls = totalISO
        healthyControlsCount = healthyISOControlsCount
        unhealthyControlsCount = unhealthyISOControlsCount
        break
      case 'SOC 2':
        totalControls = totalSOC
        healthyControlsCount = healthySOCControlsCount
        unhealthyControlsCount = unhealthySOCControlsCount
        break
      case 'GDPR':
        totalControls = totalGDPR
        healthyControlsCount = healthyGDPRControlsCount
        unhealthyControlsCount = unhealthyGDPRControlsCount
        break
      case 'HIPAA':
        totalControls = totalHIPAA
        healthyControlsCount = healthyHIPAAControlsCount
        unhealthyControlsCount = unhealthyHIPAAControlsCount
        break
      default:
        return null
    }

    return (
      <div className='col-sm-4 mb-5 mb-xl-10'>
        <div className='card card-flush h-lg-80'>
          <div className='card-header pt-0'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold text-dark'>{framework}</span>
            </h3>
          </div>

          <div className='card-body pt-0'>
            <div className='card-body d-flex align-items-end pt-0 justify-content-end px-0'>
              <div className='d-flex align-items-center flex-column  w-100 justify-content-end'>
                <div className='d-flex flex-column flex-sm-row justify-content-between fw-semibold fs-6 text-black opacity-75 w-100 mt-auto mb-2 pt-2'>
                  <span className='text-gray-800'>Controls OK</span>

                  <span className='fw-bold fs-6'>
                    {totalControls !== 0
                      ? `${((healthyControlsCount / totalControls) * 100).toFixed(0)}%`
                      : '0%'}
                  </span>
                </div>

                <div className='h-12px mx-0 w-100 rounded' style={{backgroundColor: '#DAF9EF'}}>
                  {totalControls !== 0 ? (
                    <div
                      className='rounded h-8px'
                      role='progressbar'
                      style={{
                        width: `${(healthyControlsCount / totalControls) * 100}%`,
                        backgroundColor: '#0E9F6E',
                      }}
                      aria-valuenow={(healthyControlsCount / totalControls) * 100}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  ) : (
                    <div
                      className='rounded h-8px'
                      role='progressbar'
                      style={{
                        width: `${0}%`,
                        backgroundColor: '#0E9F6E',
                      }}
                      aria-valuenow={0}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  )}
                </div>
              </div>
            </div>

            <div className='d-flex flex-stack'>
              <div className='text-gray-700 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#5E6274'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-list-check'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3.5 5.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 11.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 17.5l1.5 1.5l2.5 -2.5' />
                    <path d='M11 6l9 0' />
                    <path d='M11 12l9 0' />
                    <path d='M11 18l9 0' />
                  </svg>
                </div>
                Total Controls
              </div>

              <div className='d-flex align-items-senter'>
                <span className='text-gray-900 fw-bold fs-6'>{totalControls}</span>
              </div>
            </div>

            <div className='my-3'></div>

            <div className='d-flex flex-stack'>
              <div className='text-gray-700 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#EE3043'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M12 9v4' />
                    <path d='M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z' />
                    <path d='M12 16h.01' />
                  </svg>
                </div>
                Unhealthy
              </div>

              <div className='d-flex align-items-senter'>
                <span className='text-gray-900 fw-bold fs-6'>{unhealthyControlsCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <>
      <div className='row gx-5 gx-xl-10'>
        <div className='col-sm-4 mb-5 mb-xl-10'>
          <div className='card card-flush h-lg-80'>
            <div className='card-header pt-0'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold text-dark'>All Controls</span>
              </h3>
            </div>
            <div className='card-body pt-0'>
              <div className='card-body d-flex align-items-end pt-0 justify-content-end px-0'>
                <div className='d-flex align-items-center flex-column  w-100 justify-content-end'>
                  <div className='d-flex flex-column flex-sm-row justify-content-between fw-semibold fs-6 text-black opacity-75 w-100 mt-auto mb-2 pt-2'>
                    <span className='text-gray-800'>Controls OK</span>

                    <span className='fw-bold fs-6'>
                      {totalControl !== 0
                        ? `${((healthyControlsCount / totalControl) * 100).toFixed(0)}%`
                        : '0%'}
                    </span>
                  </div>

                  <div className='h-12px mx-0 w-100 rounded' style={{backgroundColor: '#DAF9EF'}}>
                    {totalControl !== 0 ? (
                      <div
                        className='rounded h-8px'
                        role='progressbar'
                        style={{
                          width: `${(healthyControlsCount / totalControl) * 100}%`,
                          backgroundColor: '#0E9F6E',
                        }}
                        aria-valuenow={(healthyControlsCount / totalControl) * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    ) : (
                      <div
                        className='rounded h-8px'
                        role='progressbar'
                        style={{
                          width: `${0}%`,
                          backgroundColor: '#0E9F6E',
                        }}
                        aria-valuenow={0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    )}
                  </div>
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <div className='text-gray-700 fw-semibold fs-6 me-2'>
                  <div className='d-inline-block align-middle me-2 mb-1'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='#5E6274'
                      stroke-width='2'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                      className='icon icon-tabler icons-tabler-outline icon-tabler-list-check'
                    >
                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                      <path d='M3.5 5.5l1.5 1.5l2.5 -2.5' />
                      <path d='M3.5 11.5l1.5 1.5l2.5 -2.5' />
                      <path d='M3.5 17.5l1.5 1.5l2.5 -2.5' />
                      <path d='M11 6l9 0' />
                      <path d='M11 12l9 0' />
                      <path d='M11 18l9 0' />
                    </svg>
                  </div>
                  Total Controls
                </div>

                <div className='d-flex align-items-senter'>
                  <span className='text-gray-900 fw-bold fs-6'>{totalControl}</span>
                </div>
              </div>
              <div className='my-3'></div>
              <div className='d-flex flex-stack'>
                <div className='text-gray-700 fw-semibold fs-6 me-2'>
                  <div className='d-inline-block align-middle me-2 mb-1'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='#EE3043'
                      stroke-width='2'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                      className='icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle'
                    >
                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                      <path d='M12 9v4' />
                      <path d='M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z' />
                      <path d='M12 16h.01' />
                    </svg>
                  </div>
                  Unhealthy
                </div>
                <div className='d-flex align-items-senter'>
                  <span className='text-gray-900 fw-bold fs-6'>{unhealthyControlsCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {(() => {
          const frameworksToShow =
            currentUser?.role === 'Auditor' && audits.length > 0
              ? audits.flatMap((audit) => audit.framework || [])
              : currentAuditContext
              ? currentAuditContext.framework
              : activeFrameworks

          return (
            <>
              {frameworksToShow.includes('ISO 27001:2022') && renderFrameworkCard('ISO 27001:2022')}
              {frameworksToShow.includes('SOC 2') && renderFrameworkCard('SOC 2')}
              {frameworksToShow.includes('GDPR') && renderFrameworkCard('GDPR')}
              {frameworksToShow.includes('HIPAA') && renderFrameworkCard('HIPAA')}
            </>
          )
        })()}

        {(() => {
          const frameworksToShow =
            currentUser?.role === 'Auditor' && audits.length > 0
              ? audits.flatMap((audit) => audit.framework || [])
              : currentAuditContext
              ? currentAuditContext.framework
              : activeFrameworks
          return frameworksToShow.length < 2
        })() && (
          <div className='col d-flex align-items-stretch mb-5 mb-xl-10'>
            <div className='card flex-fill'>
              <div className='card-body d-flex flex-column justify-content-center align-items-center'>
                <img src='/media/icon/framework.svg' alt='Framework icon' className='mb-4' />
                <h5 className='card-title fw-bold text-center mb-4'>
                  {(() => {
                    const frameworksToShow =
                      currentUser?.role === 'Auditor' && audits.length > 0
                        ? audits.flatMap((audit) => audit.framework || [])
                        : currentAuditContext
                        ? currentAuditContext.framework
                        : activeFrameworks
                    return frameworksToShow.length <= 0
                      ? ' Controls Unavailable'
                      : 'Enable other frameworks'
                  })()}
                </h5>
                {(() => {
                  const frameworksToShow =
                    currentUser?.role === 'Auditor' && audits.length > 0
                      ? audits.flatMap((audit) => audit.framework || [])
                      : currentAuditContext
                      ? currentAuditContext.framework
                      : activeFrameworks
                  return frameworksToShow.length <= 0
                })() ? (
                  <p className='card-text fw-semibold text-center'>
                    <span className='d-flex align-items-center'>
                      <span className='ms-4'>
                        <span className='fw-semibold fs-6 text-gray-700'>
                          Access to controls requires a framework purchase. Visit our{' '}
                          <a
                            href='https://getsecureslate.com/pricing'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary ms-1 fw-semibold'
                          >
                            Pricing Page
                          </a>{' '}
                          or{' '}
                          <span
                            style={{cursor: 'pointer', color: '#0e9f6e'}}
                            onClick={toggleCrispChat}
                            className=' ms-1 fw-semibold'
                          >
                            Contact sales
                          </span>{' '}
                          for more details.
                        </span>
                      </span>
                    </span>
                  </p>
                ) : (
                  <>
                    <p className='card-text fw-semibold text-center'>
                      <span style={{cursor: 'pointer', color: '#0e9f6e'}} onClick={toggleCrispChat} className='text-primary'>
                        Contact sales
                      </span>{' '}
                      to enable other frameworks
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ControlCard
